import _ from "lodash";
import me from "models/me";
import BaseModel from "lib/model";
import BaseCollection from "lib/collection";
import { formatCurrency } from "lib/globalize-util";

const Size = BaseModel.extend({
    props: {
        _id: "objectId",
        inventory_buckets: "array",
        original_price: {
            type: "currency",
        },
        effective_wholesale: {
            type: "currency",
        },
        effective_subtotal: {
            type: "currency",
        },
        discounted_price: {
            type: "currency",
        },
        adjusted_wholesale: {
            type: "currency",
        },
        price: {
            type: "currency",
        },
        __units_per_pack: {
            type: "number",
            default: 1,
        },
        quantity: {
            type: "number",
            default: 0,
            required: true,
        },
        size: {
            type: "string",
            required: true,
        },
        size_group: "string",
    },
    derived: {
        sizeDisplayName: {
            fn: function () {
                return this.size.replace(this.size_group || "", "");
            },
        },
    },
});

const Sizes = BaseCollection.extend({
    model: Size,
});

export default BaseModel.extend({
    props: {
        __ref: { type: "objectId", required: true },
        __images: "array",
        __media: "array",
        __item: "object", // populate product model with this
        item: "object", // populate product model with this
        __ship_start: { type: "utcDate" },
        __ship_end: { type: "utcDate" },
        __promotion: { type: "boolean", required: false },
        __exempt_from_order_surcharge: { type: "boolean", default: false },
        uses_dbp: { type: "boolean", required: false },
        total: { type: "currency", required: true, default: 0 },
        price: { type: "currency", required: true, default: 0 },
        total_quantity: { type: "number", required: true, default: 0 },
        type: "string",
        systemId: "string",
        notes: "string",
        cancelled: "boolean",
        __shipment_status: "array",
        __pack_ids: "array",
        __customizations: "array",
        __product_customizations_template_id: "string",
        proof_of_product_id: "string",
        warehouse: "string",
    },
    collections: {
        sizes: Sizes,
    },
    formatPrice(priceLookup, currency = me._activeCurrency) {
        const prices = Object.values(priceLookup);
        const maxPrice = Math.max(...prices);
        const minPrice = Math.min(...prices);

        if (!prices.length || isNaN(minPrice) || isNaN(maxPrice)) {
            return "";
        }

        return minPrice === maxPrice
            ? formatCurrency(minPrice, currency)
            : `${formatCurrency(minPrice, currency)} - ${formatCurrency(maxPrice, currency)}`;
    },
    getPrices(currency) {
        const price = this.getPriceRange("price");
        const original_wholesale = this.getPriceRange("original_price");
        const adjusted_wholesale = this.getPriceRange("adjusted_wholesale");
        const effective_wholesale = this.getPriceRange("effective_wholesale");
        const discounted_price = this.getPriceRange("discounted_price");
        return {
            // Old value to support previous implementation where no `effective_wholesale`
            price: this.formatPrice(price, currency),
            original_wholesale: this.formatPrice(original_wholesale, currency),
            adjusted_wholesale: this.formatPrice(adjusted_wholesale, currency),
            effective_wholesale: this.formatPrice(effective_wholesale, currency),
            discounted_price: this.formatPrice(discounted_price, currency),
        };
    },
    getItemTotalByDiscountedPrice() {
        return (this.sizes || []).reduce(
            (acc, size) => acc + (size.discounted_price ?? size.price) * size.quantity * size.__units_per_pack,
            0,
        );
    },
    getPriceRange(attr, sizes = this.sizes) {
        const prices = (sizes || []).map(function (size) {
            return size[attr];
        });
        const min = Math.min.apply(null, prices);
        const max = Math.max.apply(null, prices);
        // if nothing is found, return undefined (ie has no price)
        if (!isFinite(min) || !isFinite(max) || Number.isNaN(min) || Number.isNaN(max)) {
            return [undefined, undefined];
        }
        return [min, max];
    },
    getSizesByGroup: function () {
        return _(this.sizes.toJSON())
            .groupBy((size) => size.size_group || "")
            .map((sizes, name) => ({ name, sizes }))
            .value();
    },
    usesDbp() {
        return this.uses_dbp ?? false;
    },
    getPricesheetPrices(currency) {
        const pricesheetData = this?.__item?.__price_templates?.[0];
        if (pricesheetData?.__sizes?.length) {
            const pricesheetWholesalePrice = this.getPriceRange("__wholesale", pricesheetData.__sizes);
            return this.formatPrice(pricesheetWholesalePrice, currency);
        } else {
            return pricesheetData.__wholesale ? formatCurrency(pricesheetData.__wholesale, currency) : "";
        }
    },
});
